import { Navigate, useLocation } from "react-router-dom";
import { useCustomerAuth } from "./CustomerAuthContext";
import { useLang } from "@/i18n/LanguageContext";

/**
 * Gate /account routes. Redirects to /account/auth (locale-aware) when the
 * visitor is not signed in. Keeps the original target so the auth page can
 * redirect back after login.
 */
export function RequireCustomer({ children }: { children: React.ReactNode }) {
  const { user, loading } = useCustomerAuth();
  const { localePath } = useLang();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen bg-background" aria-hidden="true" />;
  }
  if (!user) {
    // Strip ?mode= from the post-login `next` target — it's only meaningful
    // for the auth page itself — and forward it to the auth route so e.g.
    // /account?mode=signup lands on the Create account tab.
    const search = new URLSearchParams(location.search);
    const mode = search.get("mode");
    search.delete("mode");
    const cleanedSearch = search.toString();
    const nextPath = `${location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ""}`;
    const params = new URLSearchParams();
    params.set("next", nextPath);
    if (mode === "signin" || mode === "signup" || mode === "forgot") {
      params.set("mode", mode);
    }
    return <Navigate to={`${localePath("/account/auth")}?${params.toString()}`} replace />;
  }
  return <>{children}</>;
}